import styled, { css, createGlobalStyle } from 'styled-components'
import { color } from 'ui/themes/utils'

import Skeleton from 'ui/blocks/Skeleton'

const statusColors = {
  success: '--add-button-button-color',
  error: '--add-button-error-color'
}

export const HostStyles = createGlobalStyle`
  :host {
    --add-button-button-color: var(--color-accent-background, ${color('branding')});
    --add-button-button-label-color: var(--color-accent-foreground, ${color('white')});

    --add-button-success-color: var(--color-green, ${color('Success/Base')});
    --add-button-error-color: var(--color-red, ${color('Danger/Base')});

    --add-button-label-weight: 700;
    --add-button-label-family: var(--font-body, sans-serif);

    --add-button-input-border-radius: 6px;
    --add-button-button-border-radius: 6px;

    --add-button-control-button-color: var(--color-typo, ${color('text')});

    --add-button-input-color: var(--color-typo, ${color('text')});
    --add-button-input-background-color: var(--color-white, ${color('white')});
    --add-button-input-border: 1px solid var(--color-border, ${color('Misc/Divider')});

    --add-button-control-height: 48px;

    --add-button-input-padding: 8px 12px;
    --add-button-input-width: 110px;

    --add-button-button-padding: 0 14px;
    --add-button-button-width: 100%;
    --add-button-button-max-width: 400px;

    --add-button-gap: 6px;
  }

  * {
    box-sizing: border-box;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;

  gap: var(--add-button-gap);

  width: 100%;
`

const ButtonLabel = styled.span`
  color: var(--add-button-button-label-color);

  font-family: var(--add-button-label-family);
  font-weight: var(--add-button-label-weight);
`

const ButtonOverlay = styled.div`
  position: absolute;

  width: 100%;
  height: 100%;

  top: 0;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    max-height: 18px;
    max-width: 18px;

    height: 100%;
    width: 100%;

    &.loading {
      animation: spin 1s linear infinite;
    }
  }
`

const Button = styled.button`
  display: block;
  position: relative;

  width: var(--add-button-button-width);
  height: var(--add-button-control-height);

  max-width: var(--add-button-button-max-width);

  padding: var(--add-button-button-padding);

  background-color: var(--add-button-button-color);
  color: var(--add-button-button-label-color);

  border: none;
  border-radius: var(--add-button-button-border-radius);

  cursor: pointer;

  &:hover {
    box-shadow: inset 0px 0px 400px 110px rgba(0, 0, 0, 0.12);
  }

  transition: all 0.2s ease-in-out;

  ${({ loading }) =>
    loading &&
    css`
      cursor: progress;

      ${ButtonLabel} {
        opacity: 0;
        visibility: hidden;
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;

      opacity: 0.6;
    `}

  ${({ status }) =>
    status &&
    css`
      background-color: var(${statusColors[status]});

      ${ButtonLabel} {
        opacity: 0;
        visibility: hidden;
      }
    `}
`

const InputWrapper = styled.div`
  position: relative;
`

const Input = styled.input`
  display: block;

  width: var(--add-button-input-width);
  height: var(--add-button-control-height);

  color: var(--add-button-input-color);
  background-color: var(--add-button-input-background-color);

  padding: var(--add-button-input-padding);
  margin: 0;

  border: var(--add-button-input-border);
  border-radius: var(--add-button-input-border-radius);

  font-family: sans-serif;

  appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
`

const Controls = styled.div`
  position: absolute;

  display: flex;
  flex-direction: column;

  top: 0;
  right: 0;
  bottom: 0;

  border-top-right-radius: var(--add-button-input-border-radius);
  border-bottom-right-radius: var(--add-button-input-border-radius);

  border-left: var(--add-button-input-border);
`

const Control = styled.button`
  flex: 1 1 auto;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 0 15px;

  border: none;

  background-color: transparent;

  cursor: pointer;

  svg {
    width: 12px;
    height: 12px;

    fill: var(--add-button-control-button-color);
    color: var(--add-button-control-button-color);
  }
`

const SkeletonContainer = styled(Wrapper)`
  ${Skeleton} {
    height: var(--add-button-control-height);
    width: var(--add-button-button-width);

    border-radius: var(--add-button-button-border-radius);
  }
`

Wrapper.Button = Button
Wrapper.ButtonLabel = ButtonLabel
Wrapper.ButtonOverlay = ButtonOverlay
Wrapper.InputWrapper = InputWrapper
Wrapper.Input = Input
Wrapper.Controls = Controls
Wrapper.Control = Control
Wrapper.SkeletonContainer = SkeletonContainer

export default Wrapper
